import { useGetList, useRedirect, useTranslate } from "ra-core";
import { Card } from "@/components/ui/card";

import type { Scoring } from "../prospects/prospectTypes";
import type { Company } from "../types";

// Driftsradar (veikartets V3): kunder med funn fra kundeskannet — HTTPS
// som mangler, treg side, utdatert plattform. Proaktiv drift: vi sier fra
// til kunden før kunden oppdager det selv. Skjult til første kundeskann
// med funn finnes.
type ScanRow = {
  id: number;
  org_nr: string;
  scan_type: string;
  scanned_at: string;
  scoring: Scoring | null;
};

export const DriftRadar = () => {
  const translate = useTranslate();
  const redirect = useRedirect();

  const { data: companies = [] } = useGetList<Company>("companies", {
    filter: {
      "tax_identifier@not.is": "null",
      "archived_at@is": "null",
      is_own_company: false,
    },
    sort: { field: "name", order: "ASC" },
    pagination: { page: 1, perPage: 500 },
  });

  const orgNrs = companies
    .map((c) => c.tax_identifier?.replace(/\s/g, "") ?? "")
    .filter((nr) => /^\d{9}$/.test(nr));

  const { data: scans = [] } = useGetList<ScanRow>(
    "entity_scans",
    {
      filter: {
        "org_nr@in": `(${orgNrs.join(",")})`,
        scan_type: "light",
      },
      sort: { field: "scanned_at", order: "DESC" },
      pagination: { page: 1, perPage: 500 },
    },
    { enabled: orgNrs.length > 0 },
  );

  const byOrgNr = new Map(
    companies.map((c) => [c.tax_identifier?.replace(/\s/g, ""), c]),
  );
  const rows = scans
    .map((scan) => ({
      company: byOrgNr.get(scan.org_nr),
      funn: (scan.scoring?.smerte ?? []).map((s) => s.t),
    }))
    .filter((r) => r.company != null && r.funn.length > 0)
    .sort((a, b) => b.funn.length - a.funn.length)
    .slice(0, 6);

  if (rows.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center">
        <h2 className="section-title">
          {translate("crm.dashboard.drift.title", { _: "Driftsradar" })}
        </h2>
      </div>
      <Card className="py-0 divide-y">
        {rows.map(({ company, funn }) => (
          <button
            key={company!.id}
            type="button"
            onClick={() => redirect(`/companies/${company!.id}/show/drift`)}
            className="flex min-h-12 w-full items-center gap-3 px-4 py-2 text-left text-sm transition-colors hover:bg-muted first:rounded-t-xl last:rounded-b-xl"
          >
            <span className="min-w-0 shrink-0 font-medium">
              {company!.name}
            </span>
            <span className="min-w-0 flex-1 truncate text-muted-foreground">
              {funn.slice(0, 2).join(" · ")}
              {funn.length > 2 ? ` (+${funn.length - 2})` : ""}
            </span>
          </button>
        ))}
      </Card>
    </div>
  );
};
